import React, { useEffect, useMemo, useState, useReducer, useRef } from 'react'
import { useGlobalState } from '@/state'
import EventRow from '@/shared/EventRow'
import Loading from '@/shared/Loading'
import { ArrowRightIcon, CalendarIcon, SearchIcon } from '@heroicons/react/solid'
import { formatInTimezone } from '@/utils/date'

import useQuery from '@/hooks/useQuery'

// Matches Dashboard / CourseIndex so every page's cards read the same.
const SECTION_CLASS = 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'
const H2_CLASS = 'text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100'

const MyEvents = (props) => {
  const [currentUser] = useGlobalState('currentUser')
  const [attendingEvents, setAttendingEvents] = useState([])
  const [pastAttendedEvents, setPastAttendedEvents] = useState([])
  const [attendedFilter, setAttendedFilter] = useState('')
  const [attendedExpanded, setAttendedExpanded] = useState(false)
  const filteredAttendedEvents = useMemo(() => {
    const q = attendedFilter.trim().toLowerCase()
    if (!q) return pastAttendedEvents
    return pastAttendedEvents.filter((event) => (
      [event.name, event.kind, ...(event.eventHosts || []).map(h => h.name)]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    ))
  }, [pastAttendedEvents, attendedFilter])

  const isComponentMounted = useRef(false)
  const [state, setState] = useReducer(
    (state, newState) => (
      {...state, ...newState}), {
        attendingEventIds: [],
        events: [],
        classSessions: []
      }
    )
  const { attendingEventIds, classSessions, events } = state
  const { getRequest } = useQuery()
  
  useEffect(() => {
    getRequest(`/api/v1/events?this_week=true`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setState( {
        events: jsonData.events,
        attendingEventIds: jsonData.attendingEventIds,
        classSessions: jsonData.classSessions
      })
    })
    getRequest(`/api/v1/events/attending`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setAttendingEvents(jsonData.attendingEvents)
    })
    getRequest(`/api/v1/events/past_attended`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setPastAttendedEvents(jsonData.pastAttendedEvents)
    })
    isComponentMounted.current = true

  }, [])

  if (!isComponentMounted.current) {
    return <Loading />
  }

  // Width and padding come from the layout container, same as Dashboard and
  // My Courses. This page used to add its own max-w-7xl on top, which capped it
  // narrower than everything else and left it visibly out of step.
  return <div className='py-8 space-y-8'>

    <div className={SECTION_CLASS}>
      <div className="relative flex justify-between">
        <h2 className={H2_CLASS}>Upcoming Events I'm Attending</h2>
      </div>

      <section aria-labelledby="events-this-week">
        <div className="relative">
          { attendingEvents.length === 0 && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 48 48" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3a1 1 0 011-1h30a1 1 0 011 1v4M8 7h32M8 7v28a2 2 0 002 2h28a2 2 0 002-2V7M16 15h16M16 19h12M16 23h16M16 27h12" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No events yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  You haven't signed up for any events yet. Browse upcoming events to find something interesting.
                </p>
                <div className="mt-6">
                  <a
                    href="/events"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Upcoming Events
                    <ArrowRightIcon className="ml-2 -mr-1 h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className="mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-3 lg:max-w-none">
            { attendingEvents.map((event, idx) => <EventRow key={`amattendevent${event.id}`} event={event} user={currentUser} attendingEventIds={[event.id.toString()]} /> )}
          </div>
        </div>
      </section>
    </div>
    {pastAttendedEvents.length > 0 && (
      <div className={SECTION_CLASS}>
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <h2 className={H2_CLASS}>Events I've Attended</h2>
          {pastAttendedEvents.length > 8 && (
            <div className='relative w-full sm:w-64'>
              <SearchIcon className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              <input
                type='search'
                value={attendedFilter}
                onChange={(e) => setAttendedFilter(e.target.value)}
                placeholder='Filter by name or host'
                aria-label="Filter events I've attended"
                className='w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1.5 pl-9 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:border-clcpurple focus:ring-1 focus:ring-clcpurple focus:outline-none'
              />
            </div>
          )}
        </div>
        <section aria-labelledby="past-attended-events" className='mt-4'>
          {/* No inner scroll on mobile. A 32rem scroll box is most of a phone
              screen, so touch scrolling gets captured by the list and the page
              underneath becomes unreachable except at the very edges. Collapsed
              it clips instead, and the button below opens it; the desktop scroll
              box is unchanged. */}
          <ul className={`${attendedExpanded ? 'max-h-none overflow-visible' : 'max-h-72 overflow-hidden'} md:max-h-[32rem] md:overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 border-t border-b border-gray-100 dark:border-gray-800`}>
            {filteredAttendedEvents.map((event) => (
              <li key={`pastattended${event.id}`} className='flex items-center justify-between gap-4 px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/40'>
                <div className='min-w-0 flex-1'>
                  <a href={`/events/${event.id}-${event.slug}`} className='block truncate text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-clcpurple'>
                    {event.name}
                  </a>
                  <div className='mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400'>
                    <CalendarIcon className='h-3.5 w-3.5 shrink-0' />
                    <span>{formatInTimezone(event.startsAtUtc, currentUser.timezone, 'MMM d, yyyy')}</span>
                    {event.kind && <span className='shrink-0'>· {event.kind}</span>}
                    {event.eventHosts && event.eventHosts.length > 0 && (
                      <span className='truncate'>· {event.eventHosts.map(h => h.name).join(', ')}</span>
                    )}
                  </div>
                </div>
                <a href={`/events/${event.id}-${event.slug}`} className='shrink-0 text-xs text-clcpurple hover:underline'>
                  View
                </a>
              </li>
            ))}
            {filteredAttendedEvents.length === 0 && (
              <li className='px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-400'>
                Nothing matches "{attendedFilter}".
              </li>
            )}
          </ul>
          {filteredAttendedEvents.length > 5 && (
            <button
              type='button'
              onClick={() => setAttendedExpanded(!attendedExpanded)}
              className='md:hidden mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-600 py-2 text-sm font-semibold text-clcpurple hover:bg-gray-50 dark:hover:bg-gray-800/40'
            >
              {attendedExpanded ? 'Show fewer' : `Show all ${filteredAttendedEvents.length}`}
            </button>
          )}
          {attendedFilter.trim() && filteredAttendedEvents.length > 0 && (
            <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
              Showing {filteredAttendedEvents.length} of {pastAttendedEvents.length}
            </p>
          )}
        </section>
      </div>
    )}
    <div className={SECTION_CLASS}>
      <div className="relative flex justify-between">
        <h2 className={H2_CLASS}>All My Upcoming Class Sessions</h2>
      </div>

      <section aria-labelledby="events-this-week">
        <div className="relative">
          { classSessions.length === 0 && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 48 48" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C12 4.56 13.79 3 16.253 3h15.494C34.21 3 36 4.56 36 6.253v13M12 6.253L8.224 9.44c-.707.707-.707 1.853 0 2.56L12 15.747m24-9.494L39.776 9.44c.707.707.707 1.853 0 2.56L36 15.747m-24 0v21.494C12 39.44 13.79 41 16.253 41h15.494C34.21 41 36 39.44 36 37.247V15.747M12 15.747l4-4h16l4 4M20 21v.01M28 21v.01M20 25v.01M28 25v.01M20 29v.01M28 29v.01M20 33v.01M28 33v.01" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No class sessions</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  You don't have any class sessions in the next two weeks. Enroll in a course to get started.
                </p>
                <div className="mt-6">
                  <a
                    href="/courses"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Enroll in Course
                    <ArrowRightIcon className="ml-2 -mr-1 h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className="mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-4 lg:max-w-none">
            { classSessions.map((event, idx) => <EventRow key={`myevent${event.id}`} event={event} user={currentUser} attendingEventIds={attendingEventIds} /> )}
          </div>
        </div>
      </section>
    </div>
  </div>
}

export default MyEvents
